"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, MessageSquareText, Sparkles, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyPoints = [{
  title: "Tailored Feasibility Assessment",
  description: "Evaluate your sample matrices, throughput requirements, and regulatory targets with our specialists."
}, {
  title: "Direct Specialist Access",
  description: "Speak directly with senior analytical chemists and qualification engineers across Ireland and the UK."
}, {
  title: "Rapid Project Scoping",
  description: "Receive practical recommendations, instrument sizing, and installation timelines within days."
}];
export default function DiscussCTA() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="discussapplicationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="relative overflow-hidden rounded-2xl bg-secondary/50 border border-border p-8 md:p-12 lg:p-16">
          {/* Subtle decorative background gradient accents */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col items-start gap-6">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary">
                <Sparkles className="size-3.5" />
                <span>Analytical Application Support</span>
              </motion.div>

              <motion.h2 variants={itemVariants} className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                Ready to review your laboratory or process requirements?
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Connect with our technical team to discuss spectroscopy, powder testing, dissolution, or process analytical technology configurations tailored to your facility.
              </motion.p>

              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2 w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                  <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                    <MessageSquareText className="size-4" />
                    <span>Discuss Your Application</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                  <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                    <span>Book a Demo</span>
                  </Link>
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="pt-2 text-xs text-muted-foreground flex items-center gap-2">
                <PhoneCall className="size-3.5 text-primary" />
                <span>Prefer a quick phone consultation? Visit our <Link to="/ContactPage" className="underline hover:text-foreground font-medium">Contact page</Link>.</span>
              </motion.div>
            </div>

            {/* Right Information Cards Column */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              {keyPoints.map((point, index) => <motion.div key={point.title} variants={itemVariants} data-index={index}>
                  <Card className="bg-card text-card-foreground border-border shadow-xs hover:shadow-md transition-shadow py-5">
                    <CardContent className="flex items-start gap-4 p-0 px-5">
                      <div className="mt-0.5 rounded-full bg-primary/10 p-1 text-primary shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <h3 className="font-default text-base font-semibold text-card-foreground">
                          {point.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-snug">
                          {point.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}